import React from 'react';
import {
  Sparkles, Users, TrendingUp, MessageCircle, Tv, Star,
  Heart, BarChart3, Share2, CheckCircle, Building2, Check
} from 'lucide-react';
import '../pdf.css';

/**
 * BrochurePDF - A4-optimized version of the Anamaya brochure for PDF generation.
 * Each <section className="pdf-page"> is exactly 1 A4 page (210x297mm).
 * The Playwright backend renders this at /brochure-pdf and exports as PDF.
 */

const Page = ({ children, className = '' }) => (
  <section className={`pdf-page ${className}`}>
    {children}
  </section>
);

const PageNumber = ({ n, total = 10 }) => (
  <div className="pdf-pagenum">
    <span>Anamaya · Guest Experience Platform</span>
    <span>{n} / {total}</span>
  </div>
);

const BrochurePDF = () => {
  return (
    <div className="pdf-root">
      {/* ───────── PAGE 1: COVER / HERO ───────── */}
      <Page className="pdf-cover">
        <div className="pdf-cover-bg">
          <div className="pdf-blob pdf-blob-1"></div>
          <div className="pdf-blob pdf-blob-2"></div>
        </div>

        <div className="pdf-cover-content">
          <div className="pdf-brand">
            <div className="pdf-brand-icon">
              <Sparkles className="w-8 h-8" style={{ color: '#fbbf24' }} />
            </div>
            <span className="pdf-brand-name">Anamaya</span>
          </div>

          <div className="pdf-cover-eyebrow">2026 · Product Brochure</div>

          <h1 className="pdf-cover-title">
            Less standardisation,<br />
            <span style={{ color: '#fbbf24' }}>more personalisation</span><br />
            with AI.
          </h1>

          <p className="pdf-cover-subtitle">
            The first guest experience platform that transforms reviews into remarkable,
            personalized moments your guests will never forget.
          </p>

          <div className="pdf-cover-footer">
            <div>
              <div className="pdf-cover-footer-label">Contact</div>
              <div className="pdf-cover-footer-value">hello@anamaya.ai</div>
            </div>
            <div>
              <div className="pdf-cover-footer-label">Web</div>
              <div className="pdf-cover-footer-value">anamaya.ai</div>
            </div>
            <div>
              <div className="pdf-cover-footer-label">Founder</div>
              <div className="pdf-cover-footer-value">Nirvaan Krishnan</div>
            </div>
          </div>
        </div>
      </Page>

      {/* ───────── PAGE 2: THE GAP + THE SOLUTION ───────── */}
      <Page>
        <div className="pdf-section">
          <div className="pdf-eyebrow">The Gap</div>
          <h2 className="pdf-h2">
            Hotels excel at operations.<br />
            <span style={{ color: '#a8a29e' }}>Guest personalisation? Not so much.</span>
          </h2>
          <p className="pdf-lead">
            Your property runs on advanced PMS, revenue management systems, and CRMs.
            Yet the most crucial element — making each guest feel truly seen — remains an afterthought.
          </p>

          <div className="pdf-gap-grid">
            {[
              { icon: Building2, label: 'PMS Systems', status: 'Advanced' },
              { icon: TrendingUp, label: 'Revenue Mgmt', status: 'Advanced' },
              { icon: Users, label: 'CRM Tools', status: 'Advanced' },
              { icon: Heart, label: 'Personalisation', status: 'Missing', highlight: true }
            ].map((item, i) => (
              <div key={i} className={`pdf-gap-card ${item.highlight ? 'pdf-gap-card-highlight' : ''}`}>
                <item.icon className="w-7 h-7" style={{ color: item.highlight ? '#d97706' : '#a8a29e' }} />
                <div className="pdf-gap-label">{item.label}</div>
                <div className={`pdf-gap-status ${item.highlight ? 'pdf-gap-status-highlight' : ''}`}>{item.status}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="pdf-section pdf-dark-section">
          <div className="pdf-eyebrow" style={{ color: '#fbbf24' }}>The Solution</div>
          <h2 className="pdf-h2" style={{ color: '#fff' }}>
            Turn guest reviews into<br />
            <span style={{ color: '#fbbf24' }}>unforgettable experiences</span>
          </h2>

          <div className="pdf-steps">
            {[
              { step: '01', title: 'Analyse', desc: 'AI extracts likes, dislikes, patterns & habits from reviews' },
              { step: '02', title: 'Generate', desc: 'Create personalized Awe Stories within your budget' },
              { step: '03', title: 'Delight', desc: 'Execute moments that turn guests into advocates' }
            ].map((item, i) => (
              <div key={i} className="pdf-step">
                <div className="pdf-step-num">{item.step}</div>
                <div className="pdf-step-title">{item.title}</div>
                <div className="pdf-step-desc">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <PageNumber n={2} />
      </Page>

      {/* ───────── PAGE 3: KEY FEATURES (6 cards) ───────── */}
      <Page>
        <div className="pdf-section">
          <div className="pdf-eyebrow">Features</div>
          <h2 className="pdf-h2">Everything you need to personalise at scale</h2>
          <p className="pdf-lead">
            A complete toolkit purpose-built for hospitality teams who want to delight every guest, every time.
          </p>

          <div className="pdf-features-grid">
            {[
              { icon: Users, title: 'Guest Preferences Directory', desc: 'Comprehensive profile for every guest — likes, dislikes, dietary preferences, room choices, celebrations.' },
              { icon: Sparkles, title: 'AI Awe Story Generator', desc: 'Budget-conscious personalization ideas that create lasting memories without breaking the bank.' },
              { icon: BarChart3, title: 'Performance Analytics', desc: 'Measure impact with hotel rankings, employee leaderboards, and story performance metrics.' },
              { icon: Share2, title: 'Social Media Automation', desc: 'Transform positive reviews into stunning social posts automatically — visuals and testimonials included.' },
              { icon: MessageCircle, title: 'WhatsApp Welcome', desc: 'Personalized WhatsApp messages that make guests feel special before they even arrive.' },
              { icon: Tv, title: 'Smart TV Greetings', desc: 'Personalized welcome messages on in-room smart TVs the moment guests enter.' }
            ].map((f, i) => (
              <div key={i} className="pdf-feature-card">
                <div className="pdf-feature-icon">
                  <f.icon className="w-6 h-6" style={{ color: '#d97706' }} />
                </div>
                <div className="pdf-feature-title">{f.title}</div>
                <div className="pdf-feature-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <PageNumber n={3} />
      </Page>

      {/* ───────── PAGE 4: PRODUCT SHOWCASE — Performance Dashboard + Guest Directory ───────── */}
      <Page className="pdf-dark-page">
        <div className="pdf-section">
          <div className="pdf-eyebrow" style={{ color: '#fbbf24' }}>Anamaya Awe</div>
          <h2 className="pdf-h2" style={{ color: '#fff' }}>
            AI-Powered <span style={{ color: '#fbbf24' }}>AWE Story Generator</span>
          </h2>
        </div>

        <div className="pdf-showcase">
          <div className="pdf-showcase-image">
            <img src="/assets/brochure/performance_dashboard.png" alt="Performance Dashboard" />
          </div>
          <div className="pdf-showcase-content">
            <h3 className="pdf-h3" style={{ color: '#fff' }}>Real-time <span style={{ color: '#fbbf24' }}>Performance Dashboard</span></h3>
            <p className="pdf-showcase-desc">
              Track WOW actions, coverage rates, top performers, and revenue impact in one intuitive dashboard.
            </p>
            <ul className="pdf-check-list">
              <li><CheckCircle className="w-4 h-4" /> WOW coverage tracking</li>
              <li><CheckCircle className="w-4 h-4" /> Top performers leaderboard</li>
              <li><CheckCircle className="w-4 h-4" /> Revenue impact analysis</li>
            </ul>
          </div>
        </div>

        <div className="pdf-showcase pdf-showcase-reverse">
          <div className="pdf-showcase-content">
            <h3 className="pdf-h3" style={{ color: '#fff' }}>Comprehensive <span style={{ color: '#fbbf24' }}>Guest Directory</span></h3>
            <p className="pdf-showcase-desc">
              Search, filter and manage your entire guest database with satisfaction scores at a glance.
            </p>
            <ul className="pdf-check-list">
              <li><CheckCircle className="w-4 h-4" /> Search by name, email, preferences</li>
              <li><CheckCircle className="w-4 h-4" /> Sort by satisfaction or reviews</li>
              <li><CheckCircle className="w-4 h-4" /> Quick stats at a glance</li>
            </ul>
          </div>
          <div className="pdf-showcase-image">
            <img src="/assets/brochure/guest_directory.png" alt="Guest Directory" />
          </div>
        </div>

        <PageNumber n={4} />
      </Page>

      {/* ───────── PAGE 5: PRODUCT SHOWCASE — Guest Profile + Hotel Rankings ───────── */}
      <Page className="pdf-dark-page">
        <div className="pdf-showcase">
          <div className="pdf-showcase-image">
            <img src="/assets/brochure/guest_profile_preferences.png" alt="Guest Profile" />
          </div>
          <div className="pdf-showcase-content">
            <h3 className="pdf-h3" style={{ color: '#fff' }}>Deep <span style={{ color: '#fbbf24' }}>Guest Profile & Preferences</span></h3>
            <p className="pdf-showcase-desc">
              Rich profile with likes, dislikes, habits, complaints, and OTA reviews aggregated in one place.
            </p>
            <ul className="pdf-check-list">
              <li><CheckCircle className="w-4 h-4" /> Likes & dislikes auto-extracted</li>
              <li><CheckCircle className="w-4 h-4" /> Habits & travel patterns</li>
              <li><CheckCircle className="w-4 h-4" /> Complaint history & resolution</li>
            </ul>
          </div>
        </div>

        <div className="pdf-showcase pdf-showcase-reverse">
          <div className="pdf-showcase-content">
            <h3 className="pdf-h3" style={{ color: '#fff' }}>Competitive <span style={{ color: '#fbbf24' }}>Hotel Rankings</span></h3>
            <p className="pdf-showcase-desc">
              See how your hotel ranks in creating Awe moments — by category, city, state, region, and globally.
            </p>
            <ul className="pdf-check-list">
              <li><CheckCircle className="w-4 h-4" /> Multi-level rankings</li>
              <li><CheckCircle className="w-4 h-4" /> WOW stories & execution score</li>
              <li><CheckCircle className="w-4 h-4" /> Achievement badges</li>
            </ul>
          </div>
          <div className="pdf-showcase-image">
            <img src="/assets/brochure/hotel_rankings.png" alt="Hotel Rankings" />
          </div>
        </div>

        <PageNumber n={5} />
      </Page>

      {/* ───────── PAGE 6: VOICE ASSISTANT + MOBILE APP ───────── */}
      <Page className="pdf-dark-page">
        <div className="pdf-showcase">
          <div className="pdf-showcase-image">
            <img src="/assets/brochure/voice_assistant.png" alt="Voice Assistant" />
          </div>
          <div className="pdf-showcase-content">
            <h3 className="pdf-h3" style={{ color: '#fff' }}>AI-Powered <span style={{ color: '#fbbf24' }}>Voice Assistant</span></h3>
            <p className="pdf-showcase-desc">
              Staff speak about a guest issue and receive instant AI-powered service recovery suggestions.
            </p>
            <ul className="pdf-check-list">
              <li><CheckCircle className="w-4 h-4" /> Voice-activated recovery</li>
              <li><CheckCircle className="w-4 h-4" /> AI-suggested gestures</li>
              <li><CheckCircle className="w-4 h-4" /> Cost estimates included</li>
            </ul>
          </div>
        </div>

        <div className="pdf-showcase pdf-showcase-reverse">
          <div className="pdf-showcase-content">
            <h3 className="pdf-h3" style={{ color: '#fff' }}>Fully <span style={{ color: '#fbbf24' }}>Mobile Compatible App</span></h3>
            <p className="pdf-showcase-desc">
              Access Anamaya on-the-go — manage guests, create stories, and track performance anywhere from any smartphone.
            </p>
            <ul className="pdf-check-list">
              <li><CheckCircle className="w-4 h-4" /> iOS & Android compatible</li>
              <li><CheckCircle className="w-4 h-4" /> Offline-first design</li>
              <li><CheckCircle className="w-4 h-4" /> Real-time sync</li>
            </ul>
          </div>
          <div className="pdf-showcase-image pdf-mobile-image">
            <div className="pdf-phone-frame">
              <img src="/assets/brochure/mobile_compatibility.png" alt="Mobile App" />
            </div>
          </div>
        </div>

        <PageNumber n={6} />
      </Page>

      {/* ───────── PAGE 7: SOCIAL MEDIA SUITE ───────── */}
      <Page>
        <div className="pdf-section">
          <div className="pdf-eyebrow" style={{ color: '#9333ea' }}>Anamaya Social</div>
          <h2 className="pdf-h2">
            Social Media <span style={{ color: '#9333ea' }}>Auto-Posting & Scheduling</span>
          </h2>
          <p className="pdf-lead">
            Turn positive guest reviews into stunning social content automatically. Plan, schedule, and manage your entire social presence from one dashboard.
          </p>
        </div>

        <div className="pdf-social-grid">
          <div className="pdf-social-card">
            <img src="/assets/brochure/social_calendar.png" alt="Schedule 365" />
            <div className="pdf-social-card-title">Schedule 365 — Year-Round Planning</div>
            <div className="pdf-social-card-desc">Plan an entire year of social posts. Monthly themes, batch approvals, AI handles the rest.</div>
          </div>
          <div className="pdf-social-card">
            <img src="/assets/brochure/social_post.png" alt="Create Post" />
            <div className="pdf-social-card-title">Create & Post</div>
            <div className="pdf-social-card-desc">Convert reviews into engaging posts with AI captions and auto-matched media.</div>
          </div>
          <div className="pdf-social-card">
            <img src="/assets/brochure/social_analytics.png" alt="Analytics" />
            <div className="pdf-social-card-title">Analytics Dashboard</div>
            <div className="pdf-social-card-desc">Track engagement, reach, and performance across all platforms with AI insights.</div>
          </div>
          <div className="pdf-social-card">
            <img src="/assets/brochure/social_ads.png" alt="Ads" />
            <div className="pdf-social-card-title">Ads Management</div>
            <div className="pdf-social-card-desc">Manage Instagram, Facebook and more campaigns from a single dashboard.</div>
          </div>
        </div>

        <div className="pdf-platforms-bar">
          <span className="pdf-platforms-label">Multi-platform support:</span>
          <span className="pdf-platforms-list">Instagram · Facebook · Twitter · LinkedIn</span>
        </div>

        <PageNumber n={7} />
      </Page>

      {/* ───────── PAGE 8: DELIVERY OPTIONS + STATS ───────── */}
      <Page>
        <div className="pdf-section">
          <div className="pdf-eyebrow">Flexible Delivery</div>
          <h2 className="pdf-h2">Personalisation for every property type</h2>
        </div>

        <div className="pdf-delivery-grid">
          <div className="pdf-delivery-card">
            <div className="pdf-delivery-icon pdf-icon-green">
              <MessageCircle className="w-6 h-6" style={{ color: '#059669' }} />
            </div>
            <div className="pdf-delivery-tag" style={{ color: '#059669' }}>Budget-Friendly</div>
            <div className="pdf-delivery-title">WhatsApp Personalisation</div>
            <div className="pdf-delivery-desc">
              Perfect for properties without smart room technology. Send heartfelt personalized messages directly to guests' phones.
            </div>
            <div className="pdf-delivery-example">
              "Dear Mr. Sharma, we remember you love our masala chai. Your favourite corner table by the garden is reserved for breakfast. Welcome back!"
            </div>
          </div>

          <div className="pdf-delivery-card">
            <div className="pdf-delivery-icon pdf-icon-purple">
              <Tv className="w-6 h-6" style={{ color: '#7c3aed' }} />
            </div>
            <div className="pdf-delivery-tag" style={{ color: '#7c3aed' }}>Premium Experience</div>
            <div className="pdf-delivery-title">Smart TV Welcome</div>
            <div className="pdf-delivery-desc">
              For hotels with in-room smart TVs. Greet guests with a personalized welcome screen the moment they enter.
            </div>
            <div className="pdf-delivery-example pdf-delivery-example-dark">
              <div style={{ color: '#fbbf24', fontSize: '10px', marginBottom: '4px' }}>Welcome Back</div>
              <div style={{ fontSize: '18px', fontWeight: '300', color: '#fff' }}>Mrs. Priya Mehta</div>
              <div style={{ color: '#a8a29e', fontSize: '10px', marginTop: '4px' }}>Happy Anniversary! We've arranged roses in your room.</div>
            </div>
          </div>
        </div>

        <div className="pdf-stats-bar">
          {[
            { value: '40%', label: 'Increase in positive reviews' },
            { value: '3.2x', label: 'Higher guest retention' },
            { value: '25%', label: 'More direct bookings' },
            { value: '< 2 min', label: 'To generate Awe Story' }
          ].map((s, i) => (
            <div key={i} className="pdf-stat">
              <div className="pdf-stat-value">{s.value}</div>
              <div className="pdf-stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="pdf-testimonial">
          <Star className="w-7 h-7" style={{ color: '#fbbf24' }} />
          <blockquote className="pdf-testimonial-quote">
            "Anamaya transformed how we connect with guests. Our team now knows exactly what makes each guest tick, and our reviews have never been better."
          </blockquote>
          <div className="pdf-testimonial-author">
            <div className="pdf-testimonial-avatar"></div>
            <div>
              <div className="pdf-testimonial-name">Bhashker Singh</div>
              <div className="pdf-testimonial-role">General Manager, The Golden Sunrise Hotel</div>
            </div>
          </div>
        </div>

        <PageNumber n={8} />
      </Page>

      {/* ───────── PAGE 9: PRICING ───────── */}
      <Page>
        <div className="pdf-section">
          <div className="pdf-eyebrow">Pricing</div>
          <h2 className="pdf-h2">Simple, transparent pricing</h2>
          <p className="pdf-lead">Choose the plan that fits your property. No hidden fees, no surprises.</p>
        </div>

        <div className="pdf-pricing-grid">
          <div className="pdf-price-card">
            <div className="pdf-price-tier">Starter</div>
            <div className="pdf-price-value">₹30K<span>/month</span></div>
            <p className="pdf-price-desc">Perfect for boutique hotels getting started with guest personalisation.</p>
            <ul className="pdf-price-features">
              {['Guest Preferences Directory', 'AI Awe Story Generator', 'Budget Controls', 'Employee Leaderboard', 'Hotel Rankings', 'Basic Analytics', 'Email Support'].map((f, i) => (
                <li key={i}><Check className="w-3 h-3" style={{ color: '#f59e0b' }} /> {f}</li>
              ))}
            </ul>
          </div>

          <div className="pdf-price-card pdf-price-card-featured">
            <div className="pdf-price-badge">MOST POPULAR</div>
            <div className="pdf-price-tier" style={{ color: '#fbbf24' }}>Professional</div>
            <div className="pdf-price-value" style={{ color: '#fff' }}>₹45K<span style={{ color: '#a8a29e' }}>/month</span></div>
            <p className="pdf-price-desc" style={{ color: '#d6d3d1' }}>For growing hotels that want the complete personalisation suite.</p>
            <ul className="pdf-price-features" style={{ color: '#d6d3d1' }}>
              {['Everything in Starter', 'Social Media Auto-posting', 'WhatsApp Personalisation', 'Smart TV Greetings', 'Advanced Analytics', 'Multi-property Support', 'API Integrations', 'Priority Support'].map((f, i) => (
                <li key={i}><Check className="w-3 h-3" style={{ color: '#fbbf24' }} /> {f}</li>
              ))}
            </ul>
          </div>

          <div className="pdf-price-card">
            <div className="pdf-price-tier">Enterprise</div>
            <div className="pdf-price-value">Custom</div>
            <p className="pdf-price-desc">For hotel chains and large properties with custom requirements.</p>
            <ul className="pdf-price-features">
              {['Everything in Professional', 'Unlimited Properties', 'Custom Integrations', 'Dedicated Account Manager', 'On-premise Deployment', 'Custom AI Training', 'SLA Guarantee', '24/7 Phone Support'].map((f, i) => (
                <li key={i}><Check className="w-3 h-3" style={{ color: '#f59e0b' }} /> {f}</li>
              ))}
            </ul>
          </div>
        </div>

        <PageNumber n={9} />
      </Page>

      {/* ───────── PAGE 10: FOUNDER + DEMO REQUEST + CTA ───────── */}
      <Page>
        <div className="pdf-founder">
          <div className="pdf-founder-photo">
            <img src="/assets/brochure/founder.jpg" alt="Nirvaan Krishnan" />
          </div>
          <div className="pdf-founder-content">
            <h3 className="pdf-founder-quote">"Hospitality is the art of anticipation and acknowledgement"</h3>
            <p className="pdf-founder-text">
              Anamaya is the layer that makes guests feel known — even at scale. Less standardisation, more noticing.
            </p>
            <div className="pdf-founder-name">— Nirvaan Krishnan <span>· Founder & CEO</span></div>
          </div>
        </div>

        <div className="pdf-demo-section">
          <div className="pdf-eyebrow">Request a Demo</div>
          <h2 className="pdf-h2" style={{ fontSize: '24px' }}>See Anamaya in action</h2>
          <p className="pdf-lead" style={{ fontSize: '12px' }}>
            Schedule a personalized 30-minute demo with our team. Custom use cases, ROI discussion, and Q&A.
          </p>

          <div className="pdf-demo-form">
            <div className="pdf-form-row">
              <div className="pdf-form-field">
                <label>Your Name</label>
                <div className="pdf-form-input">_____________________________</div>
              </div>
              <div className="pdf-form-field">
                <label>Email</label>
                <div className="pdf-form-input">_____________________________</div>
              </div>
            </div>
            <div className="pdf-form-row">
              <div className="pdf-form-field">
                <label>Hotel / Property Name</label>
                <div className="pdf-form-input">_____________________________</div>
              </div>
              <div className="pdf-form-field">
                <label>Number of Rooms</label>
                <div className="pdf-form-input">_____________________________</div>
              </div>
            </div>
            <div className="pdf-form-row">
              <div className="pdf-form-field" style={{ flex: 1 }}>
                <label>Phone Number</label>
                <div className="pdf-form-input">_____________________________</div>
              </div>
            </div>
            <div className="pdf-form-cta">
              <div>
                <div className="pdf-form-cta-label">Or contact us directly</div>
                <div className="pdf-form-cta-value">hello@anamaya.ai · +91 98765 43210</div>
              </div>
              <div className="pdf-form-cta-btn">Request Demo →</div>
            </div>
          </div>
        </div>

        <div className="pdf-final-cta">
          <Sparkles className="w-6 h-6" style={{ color: '#fbbf24' }} />
          <div className="pdf-final-cta-text">
            Ready to create unforgettable guest experiences? Visit <strong>anamaya.ai</strong> or scan to schedule a demo.
          </div>
        </div>

        <PageNumber n={10} />
      </Page>
    </div>
  );
};

export default BrochurePDF;
